import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Upload, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownSelect } from '@/components/ui/input';
import { api } from '@/lib/utils';

const AGREEMENT_FIELDS = [
  '',
  'customer_name',
  'customer_email',
  'customer_phone',
  'plan_name',
  'amount',
  'end_date',
  'start_date',
  'last_service_date',
  'last_invoice_date',
  'unpaid_invoice',
  'external_ref',
  'technician_name',
] as const;

const AGREEMENT_FIELD_OPTIONS = AGREEMENT_FIELDS.map((opt) => ({
  value: opt,
  label: opt ? opt.replace(/_/g, ' ') : '— skip —',
}));

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; message: string }>;
}

type ImportStep = 'upload' | 'preview' | 'done';

export function OnboardingCsvImport({
  onSuccess,
  onBack,
}: {
  onSuccess: (result: ImportResult) => void;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [step, setStep] = useState<ImportStep>('upload');
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [preview, setPreview] = useState<{
    headers: string[];
    suggestedMapping: Record<string, string | null>;
    previewRows: Array<{ row: number; valid: boolean; issues: string[] }>;
    validRows: number;
    invalidRows: number;
  } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const previewMutation = useMutation({
    mutationFn: (payload: { csv: string; mapping?: Record<string, string | null> }) =>
      api<NonNullable<typeof preview>>('/api/v1/import/agreements/preview', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (res) => {
      setPreview(res);
      setMapping(res.suggestedMapping);
      setStep('preview');
    },
  });

  const importMutation = useMutation({
    mutationFn: () =>
      api<ImportResult>('/api/v1/import/agreements', {
        method: 'POST',
        body: JSON.stringify({ csv, mapping, dedupe_mode: 'create_only' }),
      }),
    onSuccess: (res) => {
      setResult(res);
      setStep('done');
      if (res.created > 0 || res.updated > 0) {
        void qc.invalidateQueries({ queryKey: ['feedback-prompt'] });
      }
      onSuccess(res);
    },
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      setCsv(text);
      previewMutation.mutate({ csv: text });
    };
    reader.readAsText(file);
  };

  const mappedCount = preview
    ? Object.values(mapping).filter(Boolean).length
    : 0;

  if (step === 'done' && result) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Imported {result.created} agreement{result.created === 1 ? '' : 's'}.
            {result.skipped > 0 && ` ${result.skipped} row${result.skipped === 1 ? '' : 's'} skipped.`}
          </div>
        </div>
        {result.errors.length > 0 && (
          <div className="max-h-32 overflow-y-auto rounded-lg border border-border text-[13px]">
            {result.errors.map((e) => (
              <div key={e.row} className="flex gap-2 border-b border-border px-3 py-1.5 last:border-0">
                <span className="font-medium text-text-secondary">Row {e.row}</span>
                <span className="text-text-muted">{e.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (step === 'preview' && preview) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-lg bg-brand-subtle/60 px-3 py-2.5 text-[13px] text-text-secondary">
          <Wand2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <span>
            We auto-mapped {mappedCount} column{mappedCount === 1 ? '' : 's'} from your file. Adjust anything that looks off, then import.
          </span>
        </div>
        <div className="rounded-lg bg-bg-muted px-3 py-2 text-[13px] text-text-secondary">
          {preview.validRows} ready to import
          {preview.invalidRows > 0 && ` · ${preview.invalidRows} with issues (skipped)`}
        </div>
        <div className="max-h-40 space-y-2 overflow-y-auto">
          {preview.headers.map((header) => (
            <div key={header} className="grid grid-cols-2 items-center gap-2 text-[13px]">
              <span className="truncate font-mono text-text-muted">{header}</span>
              <DropdownSelect
                value={mapping[header] ?? ''}
                options={AGREEMENT_FIELD_OPTIONS}
                className="h-9 text-[13px]"
                onChange={(next) => setMapping((m) => ({ ...m, [header]: next || null }))}
              />
            </div>
          ))}
        </div>
        {importMutation.isError && (
          <p className="text-[13px] text-danger">{(importMutation.error as Error).message}</p>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setStep('upload')}>
            Choose another file
          </Button>
          <Button
            onClick={() => importMutation.mutate()}
            loading={importMutation.isPending}
            disabled={preview.validRows === 0}
            className="flex-1"
          >
            Import {preview.validRows} agreement{preview.validRows === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border bg-bg-subtle/40 px-4 py-8 transition-colors hover:border-brand hover:bg-brand-subtle/30"
      >
        <Upload className="h-8 w-8 text-brand" />
        <span className="text-sm font-medium">Upload your spreadsheet</span>
        <span className="text-[13px] text-text-secondary">CSV export from Jobber, QuickBooks, or Excel</span>
      </button>

      <div>
        <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">Or paste CSV</label>
        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={5}
          placeholder={`customer_name,customer_email,plan_name,amount,end_date\nJane Doe,jane@example.com,Comfort Plan,300,2026-07-10`}
          className="w-full rounded-lg border border-border bg-bg-surface px-3 py-2 font-mono text-xs leading-relaxed transition-colors placeholder:text-text-muted focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25"
        />
      </div>

      {previewMutation.isError && (
        <p className="text-[13px] text-danger">{(previewMutation.error as Error).message}</p>
      )}

      <div className="flex gap-2">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={() => previewMutation.mutate({ csv })}
          loading={previewMutation.isPending}
          disabled={!csv.trim()}
          className="flex-1"
        >
          Preview import
        </Button>
      </div>
    </div>
  );
}
